// import '@ant-design/compatible/assets/index.css';


import { useQuery } from 'react-query';
import React, { useCallback, useEffect, useMemo } from 'react';

import { Spin, Popconfirm, Space, DatePicker } from 'antd';
import DF from 'DisplayFramework';
import { toCurrency } from 'utils/utils';

import Form, { FormInstance } from 'antd/lib/form';

import { PlusOutlined } from '@ant-design/icons';

import InputCurrency from 'components/InputCurrency';
import { useFormList } from 'hooks/useFormList';
import _, { isNil, sumBy } from 'lodash';
import moment from 'moment';
import DataGrid, { DataGridColDef } from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import {
  dateToString,
  indexBy,
  shouldUpdateCompare,
  toDatePickerDate,
} from 'utils/utils2';
import Value from 'components/Value';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import {
  getSupplierInvoiceById,
  SuppInvoiceScheduleResponse,
} from 'services/supplierInvoice';
import SIBeneficiarySelect from 'pages/SupplierInvoice/SIDetail/components/SIBeneficiarySelect';
import FormInput from 'components/FormInput';
import ParBankAccountSelect from 'components/ParBankAccountSelect';

export type TabSIPayScheduleProps = {
  objectid: number;
  form: FormInstance;
  setSuccessful: any;
};

const TabSIPaySchedule = ({
  objectid,
  form,
  setSuccessful,
}: TabSIPayScheduleProps) => {
  const fieldName = 'suppInvoiceScheduleViewList';

  const { add, remove } = useFormList(form, fieldName, {
    addAsFirst: true,
    idKey: 'sisid',
  });

  const { data: supplierInvoice, isLoading } = useQuery(
    ['getSupplierInvoiceById', { siid: objectid }],
    () =>
      getSupplierInvoiceById({ siid: objectid }).then((res) => res.data ?? {}),
    {
      enabled: !isNil(objectid),
    }
  );

  useEffect(() => {
    setSuccessful(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (supplierInvoice) {
      form.setFieldsValue({
        [fieldName]: supplierInvoice.suppInvoiceSchedules.map((line) => ({
          ...line,
          sisduedate: toDatePickerDate(line.sisduedate),
        })),
        supplier: {
          tpname: supplierInvoice.suppliername,
          tpreference: supplierInvoice.tprefsupplier,
          tpid: supplierInvoice.tpidsupplier,
        },
        tppid: supplierInvoice.tpidsupplier,
        sigrosstotal: supplierInvoice.sigrosstotal,
        sisamount: sumBy(supplierInvoice.suppInvoiceSchedules, 'sisamount'),
        currcode: supplierInvoice.currcode,
      });
      form.setFieldsValue({
        sigrosstotal: supplierInvoice.sigrosstotal,
        [fieldName]: supplierInvoice.suppInvoiceSchedules.map((line) => ({
          ...line,
          sisduedate: toDatePickerDate(line.sisduedate),
        })),
      });
    } else
      form.setFieldsValue({
        [fieldName]: [],
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectid, supplierInvoice, form]);

  const mgidd = localStorage.getItem('mgid');
  const getName = useCallback(
    (name: string) => (record: SuppInvoiceScheduleResponse) => {
      const schedules = form.getFieldValue(fieldName);
      const index = indexBy(schedules, 'sisid')[record.sisid];
      return [fieldName, index, name];
    },
    [form]
  );
  const addRow = () => {
    const totalInvoiceAmount: number = form.getFieldValue('sigrosstotal');
    const totalPaymentsScheduled: number = form.getFieldValue('sisamount');
    let leftAmount: number = totalInvoiceAmount;

    if (sumBy(fieldName, 'sisamount') && totalPaymentsScheduled) {
      leftAmount = totalPaymentsScheduled - totalInvoiceAmount;
    }

    const sisamount = totalPaymentsScheduled
      ? leftAmount - totalPaymentsScheduled
      : leftAmount;
    const newsisorder = form.getFieldValue(fieldName).length + 1;

    const item: any = {
      siscategory: '',
      sislabel: '',
      sisid: Math.round(_.random(10000) * 10000000),
      sisamount: sisamount,
      new: true,
      sisdueamount: sisamount,
      sisduedate: moment() as any,
      sisorder: newsisorder,
      sispaymentdelaycode: '',
      sispaymentmode: '',
      sisstatus: 'INIT',
      sistype: 'PAYMENT',
      mgbankaccount: '' as any,
      tpidbeneficiary: '' as any,
      tpbeneficiarybankaccount: '' as any,
      sispercentage: 0,
    };
    add(item);
    recalculate(0, { sisamount: sisamount });
  };

  const recalculate = useCallback(
    (index: number, updatedValues: any) => {
      const values = form.getFieldValue(
        fieldName
      ) as SuppInvoiceScheduleResponse[];
      let newValues = values.map((item, i) => {
        if (i !== index) {
          return item;
        }

        const updatedItem = { ...item, ...updatedValues };

        const sisamount = updatedItem.sisamount;
        const sisdueamount = updatedItem.sisamount;

        return {
          ...updatedItem,
          sisamount,
          sisdueamount,
        };
      });
      let sisamount = sumBy(newValues, 'sisamount');
      if (sisamount > form.getFieldValue('sigrosstotal')) {
        let amounttoreduce = sisamount - form.getFieldValue('sigrosstotal');
        newValues = newValues.map((item, i) => {
          if (i === index) return item;

          if (item.sisamount > amounttoreduce && amounttoreduce > 0) {
            const amount = amounttoreduce;
            amounttoreduce = 0;
            return {
              ...item,
              sisamount: item.sisamount - amount,
              sisdueamount: item.sisamount - amount,
            };
          } else return { ...item };
        });
        sisamount = sumBy(newValues, 'sisamount');
      }

      form.setFieldsValue({
        [fieldName]: newValues,
        sisamount,
      });
    },
    [form, form, supplierInvoice]
  );

  const insertbeneficiary = (obj: any, index: any) => {
    const values = form.getFieldValue(
      fieldName
    ) as SuppInvoiceScheduleResponse[];
    const newValues = values.map((item, i) => {
      if (i !== index) {
        return item;
      }

      let newDate = '' as any;
      newDate = moment().add(
        obj?.tppaydelayout
          ?.replace('M', '')
          .substr(1, obj?.tppaydelayout?.length),
        'days'
      );

      if (obj?.tppaydelayout?.indexOf('M') > -1) {
        newDate = moment().endOf('month');
      }
      const updatedItem = {
        ...item,
        tpbeneficiarybankaccount: obj?.tpbklist.find(
          (bk: any) =>
            bk.bkstatus === 'VALID' && ['INOUT', 'OUT'].includes(bk.bktype)
        )?.tpbkid,
        sispaymentdelaycode: obj?.tppaydelayout,
        sispaymentmode: obj?.tppaymodeout,
        tpidbeneficiary: obj?.tpid,
        sisduedate: newDate,
      };

      const sisamount = updatedItem.sisamount;
      const sisdueamount = updatedItem.sisamount;

      return {
        ...updatedItem,
        sisamount,
        sisdueamount,
      };
    });
    form.setFieldsValue({
      [fieldName]: newValues,
    });
  };
  if (form.getFieldValue(fieldName) === undefined) {
    form.setFieldsValue({
      [fieldName]: [],
    });
  }
  const columns = useMemo<DataGridColDef<SuppInvoiceScheduleResponse>[]>(
    () => [
      {
        comcode: 'SILinesSchedule.sistype',
        key: 'siltype',
        width: '13%',
        fixed: 'left',
        render: (_, record, index) => (
          <Form.Item name={getName('sistype')(record)}>
            <ParListSelect
              parcode="SISTYPE"
              disabled={
                record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.tpidbeneficiary',
        key: 'tpidbeneficiary',
        width: '18%',
        render: (val, record, index) => (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => (
              <FormInput
                name={getName('tpidbeneficiary')(record)}
                rules={[{ required: true }]}
                noStyle
              >
                <SIBeneficiarySelect
                  allowClear
                  onSelected={(e) => {
                    insertbeneficiary(e, index);
                  }}
                  tpid={getFieldValue('tppid')}
                  disabled={
                    record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
                  }
                />
              </FormInput>
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.sisamount',
        dataIndex: 'sisamount',
        width: '15%',
        align: 'right',
        render: (_value, _record, index) => (
          <Form.Item
            name={getName('sisamount')(_record)}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue(getName('sisamount')(_record)) <= 0) {
                    return Promise.reject(new Error('sisamount should be > 0'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputCurrency
              onChange={(value) => recalculate(index, { sisamount: value })}
              currcode={form.getFieldValue('currcode')}
              disabled={
                _record.sisstatus !== 'VALID' && _record.sisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.sispaymentdelaycode',
        key: 'sispaymentdelaycode',
        width: '20%',
        render: (_, record, index) => (
          <Form.Item
            name={getName('sispaymentdelaycode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="PAYDELAYOU"
              onSelected={(value) => {
                let newDate = '' as any;
                newDate = moment().add(
                  value.replace('M', '').substr(1, value.length),
                  'days'
                );

                if (value.indexOf('M') > -1) {
                  newDate = moment()
                    .add(value.replace('M', '').substr(1, value.length), 'days')
                    .endOf('month');
                }
                recalculate(index, {
                  sisduedate: newDate,
                });
              }}
              disabled={
                record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.sisduedate',
        key: 'sisduedate',
        width: '12%',
        render: (_, record, index) => (
          <Form.Item name={getName('sisduedate')(record)}>
            {record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT' ? (
              dateToString(moment(record.sisduedate))
            ) : (
              <DatePicker />
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.sispaymentmode',
        key: 'sispaymentmode',
        width: '13%',
        render: (_, record, index) => (
          <Form.Item
            name={getName('sispaymentmode')(record)}
            rules={[{ required: true }]}
          >
            <ParListSelect
              parcode="PAYMODEIN"
              disabled={
                record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
              }
            />
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.tpbeneficiarybankaccount',
        key: 'tpbeneficiarybankaccount',
        width: '20%',
        render: (_, record, index) => (
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item name={getName('tpbeneficiarybankaccount')(record)}>
                <SIBeneficiarySelect
                  bankaccount
                  tpid={getFieldValue('tppid')}
                  disabled={
                    record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.mgbankaccount',
        key: 'mgbankaccount',
        width: '20%',
        render: (_, record, index) => (
          <Form.Item shouldUpdate={shouldUpdateCompare('currcode')} noStyle>
            {({ getFieldValue }) => (
              <Form.Item name={getName('mgbankaccount')(record)}>
                <ParBankAccountSelect
                  mgid={Number(mgidd)}
                  direction="OUT"
                  disabled={
                    record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
                  }
                  currcode={getFieldValue('currcode') ?? ''}
                />
              </Form.Item>
            )}
          </Form.Item>
        ),
      },
      {
        comcode: 'SILinesSchedule.sisstatus',
        key: 'sisstatus',
        width: '10%',
        dataIndex: 'sisstatus',
        align: 'right',
        render: (_, _record, index) => {
          return (
            <Form.Item name={getName('sisstatus')(_record)}>
              <Value />
            </Form.Item>
          );
        },
      },
      {
        comcode: 'SILinesSchedule.sisdueamount',
        key: 'sisdueamount',
        render: (_value, _record, index) => (
          <Form.Item name={getName('sisdueamount')(_record)}>
            <Value
              formatter={(val) =>
                toCurrency(val, form.getFieldValue('currcode'))
              }
            />
          </Form.Item>
        ),
        width: '13%',
        align: 'right',
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record) => (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => {
              form.setFieldsValue({
                sisamount: form.getFieldValue('sisamount') - record.sisamount,
              });
              remove(record.sisid);
            }}
          >
            <DeleteButton
              onlyIcon
              disabled={
                record.sisstatus !== 'VALID' && record.sisstatus !== 'INIT'
              }
            />
          </Popconfirm>
        ),
        align: 'center',
        width: 100,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mgidd, recalculate, objectid, form]
  );

  const title = (
    <Form.Item
      noStyle
      shouldUpdate={shouldUpdateCompare([
        'sisamount',
        'sigrosstotal',
        'currcode',
      ])}
    >
      {({ getFieldValue }) => {
        return (
          <Space size={16}>
            <span style={{ color: '#668cff' }}>
              Total Invoice Amount :{' '}
              {toCurrency(
                getFieldValue('sigrosstotal'),
                getFieldValue('currcode')
              )}
            </span>
            <strong
              style={{
                color:
                  getFieldValue('sisamount') === getFieldValue('sigrosstotal')
                    ? 'green'
                    : getFieldValue('sisamount') > getFieldValue('sigrosstotal')
                    ? 'red'
                    : 'orange',
              }}
            >
              Total Payments Scheduled:{' '}
              {toCurrency(
                _.sumBy(getFieldValue(fieldName), 'sisamount'),
                getFieldValue('currcode')
              )}
            </strong>
          </Space>
        );
      }}
    </Form.Item>
  );

  const getTotalSisamount = () => {
    return _.sumBy(form.getFieldValue(fieldName), 'sisamount');
  };

  return (
    <DF.Provider pagcode="tabcipay">
      <Form.Item
        name="suppInvoiceScheduleViewList"
        shouldUpdate
        rules={[
          {
            required: true,
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (getFieldValue('sigrosstotal') !== getTotalSisamount()) {
                return Promise.reject(
                  new Error(
                    'Total Invoice Amount should be equal to Total Payments Scheduled'
                  )
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      />
      <Form
        form={form}
        onValuesChange={(values) => {
          form?.setFieldsValue({
            [fieldName]: form.getFieldValue([fieldName]),
          });
          form?.setFieldsValue({
            [fieldName]: values,
          });
        }}
      >
        <Spin spinning={isLoading}>
          <Form.Item
            noStyle
            shouldUpdate={shouldUpdateCompare('suppInvoiceScheduleViewList')}
          >
            {({ getFieldValue }) => (
              <DataGrid
                title={title}
                columns={columns}
                dataSource={getFieldValue(fieldName)}
                rowKey="sisid"
                hideFilters
                extraActions={
                  <ButtonOperation
                    icon={<PlusOutlined />}
                    type="primary"
                    onClick={addRow}
                    comcode="add"
                  />
                }
              />
            )}
          </Form.Item>
        </Spin>
      </Form>
    </DF.Provider>
  );
};
export default TabSIPaySchedule;
